import { Autocomplete, Card, CardContent, CircularProgress, Grid, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import type { DateTime } from "luxon";
import { type FC, useEffect, useRef, useState } from "react";
import { SystemDatePickerPopup } from "../../components/UIToolkit/Popups/SystemDatePickerPopup";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import type { PBJFacilityInfo, PbjCalculatorParams, PbjCalculatorResponse } from "../../utils/models";
import { useSearchPbjFacilities } from "./hooks";

type PbjCalculatorHeaderProps = {
	startMonth: DateTime;
	setStartMonth: (value: DateTime) => void;
	endMonth: DateTime;
	setEndMonth: (value: DateTime) => void;
	onCalculate: (params: PbjCalculatorParams) => void;
	isPending: boolean;
	calculatedData?: PbjCalculatorResponse;
};

export const PbjCalculatorHeader: FC<PbjCalculatorHeaderProps> = ({
	startMonth,
	setStartMonth,
	endMonth,
	setEndMonth,
	onCalculate,
	isPending,
	calculatedData,
}) => {
	const [selectedFacility, setSelectedFacility] = useState<PBJFacilityInfo | null>(null);
	const [facilityInputValue, setFacilityInputValue] = useState<string>("");

	const { data: facilityOptions = [], isLoading: facilityLoading } = useSearchPbjFacilities(facilityInputValue);

	const [rnWage, setRnWage] = useState<number>(40);
	const [lpnWage, setLpnWage] = useState<number>(35);
	const [cnaWage, setCnaWage] = useState<number>(20);
	const [rnPercentage, setRnPercentage] = useState<number>(33.33);
	const [lpnPercentage, setLpnPercentage] = useState<number>(33.33);
	const [cnaPercentage, setCnaPercentage] = useState<number>(33.33);

	// Update percentages when calculated data is available
	useEffect(() => {
		if (calculatedData) {
			setRnPercentage(calculatedData.calculated_rn_percentage);
			setLpnPercentage(calculatedData.calculated_lpn_percentage);
			setCnaPercentage(calculatedData.calculated_cna_percentage);
		}
	}, [calculatedData]);
	const [otPercentage, setOtPercentage] = useState<number>(5);
	const [otCostPercentage, setOtCostPercentage] = useState<number>(1.5);
	const [agencyCostPercentage, setAgencyCostPercentage] = useState<number>(200);
	const [hppdLowerThreshold, setHppdLowerThreshold] = useState<number>(2.8);
	const [hppdUpperThreshold, setHppdUpperThreshold] = useState<number>(3.2);
	const [targetOtReduction, setTargetOtReduction] = useState<number>(60);
	const [targetOverstaffedReduction, setTargetOverstaffedReduction] = useState<number>(60);
	const [targetAgencyReduction, setTargetAgencyReduction] = useState<number>(80);
	const [startMonthPickerOpen, setStartMonthPickerOpen] = useState<boolean>(false);
	const [endMonthPickerOpen, setEndMonthPickerOpen] = useState<boolean>(false);
	const startMonthRef = useRef<HTMLDivElement>(null);
	const endMonthRef = useRef<HTMLDivElement>(null);

	const handleCalculate = (): void => {
		if (!selectedFacility) return;

		onCalculate({
			facility_identifier: selectedFacility.provnum,
			start_month: startMonth.toFormat("yyyy-MM"),
			end_month: endMonth.toFormat("yyyy-MM"),
			rn_wage: rnWage,
			lpn_wage: lpnWage,
			cna_wage: cnaWage,
			rn_percentage: rnPercentage,
			lpn_percentage: lpnPercentage,
			cna_percentage: cnaPercentage,
			ot_percentage: otPercentage,
			ot_cost_percentage: otCostPercentage,
			agency_cost_percentage: agencyCostPercentage,
			hppd_lower_threshold: hppdLowerThreshold,
			hppd_upper_threshold: hppdUpperThreshold,
			target_ot_reduction: targetOtReduction,
			target_overstaffed_reduction: targetOverstaffedReduction,
			target_agency_reduction: targetAgencyReduction,
		});
	};
	return (
		<Box>
			<Typography color="primary" variant="h4" gutterBottom>
				ROI Calculator - HPPD & PPD Analysis
			</Typography>
			<Card sx={{ mb: 3 }}>
				<CardContent>
					<Grid container spacing={2}>
						<Grid size={{ xs: 12, md: 6 }}>
							<Autocomplete
								fullWidth
								options={facilityOptions}
								value={selectedFacility}
								onChange={(_, newValue) => setSelectedFacility(newValue)}
								inputValue={facilityInputValue}
								onInputChange={(_, newInputValue) => setFacilityInputValue(newInputValue)}
								getOptionLabel={(option) => `${option.provname} (${option.city}, ${option.state})`}
								getOptionKey={(option) => option.provnum}
								loading={facilityLoading}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Facility Name"
										placeholder="Search by facility name"
										required
										slotProps={{
											...params.slotProps,

											input: {
												...params.slotProps.input,
												endAdornment: (
													<>
														{facilityLoading ? <CircularProgress size={20} /> : null}
														{params.slotProps.input.endAdornment}
													</>
												),
											},
										}}
									/>
								)}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 3 }}>
							<TextField
								fullWidth
								label="Start Month"
								value={startMonth.toFormat("yyyy-MM")}
								onClick={() => setStartMonthPickerOpen(true)}
								slotProps={{ inputLabel: { shrink: true }, htmlInput: { readOnly: true } }}
								ref={startMonthRef}
								sx={{ cursor: "pointer" }}
							/>
							<SystemDatePickerPopup
								open={startMonthPickerOpen}
								onClose={() => setStartMonthPickerOpen(false)}
								anchorEl={startMonthRef.current}
								value={startMonth}
								onChange={(newValue) => newValue && setStartMonth(newValue)}
								overridePickerProps={{ views: ["year", "month"], openTo: "month" }}
								hideTextField
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 3 }}>
							<TextField
								fullWidth
								label="End Month"
								value={endMonth.toFormat("yyyy-MM")}
								onClick={() => setEndMonthPickerOpen(true)}
								slotProps={{ inputLabel: { shrink: true }, htmlInput: { readOnly: true } }}
								ref={endMonthRef}
								sx={{ cursor: "pointer" }}
							/>
							<SystemDatePickerPopup
								open={endMonthPickerOpen}
								onClose={() => setEndMonthPickerOpen(false)}
								anchorEl={endMonthRef.current}
								value={endMonth}
								onChange={(newValue) => newValue && setEndMonth(newValue)}
								overridePickerProps={{ views: ["year", "month"], openTo: "month" }}
								hideTextField
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="RN Base Wage"
								type="number"
								value={rnWage}
								onChange={(e) => setRnWage(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="LPN Base Wage"
								type="number"
								value={lpnWage}
								onChange={(e) => setLpnWage(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="CNA Base Wage"
								type="number"
								value={cnaWage}
								onChange={(e) => setCnaWage(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="RN %"
								type="number"
								value={rnPercentage}
								onChange={(e) => setRnPercentage(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, max: 100, step: 0.01 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="LPN %"
								type="number"
								value={lpnPercentage}
								onChange={(e) => setLpnPercentage(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, max: 100, step: 0.01 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="CNA %"
								type="number"
								value={cnaPercentage}
								onChange={(e) => setCnaPercentage(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, max: 100, step: 0.01 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="OT %"
								type="number"
								value={otPercentage}
								onChange={(e) => setOtPercentage(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, max: 100, step: 1 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="OT Pay Increase (e.g., 1.5)"
								type="number"
								value={otCostPercentage}
								onChange={(e) => setOtCostPercentage(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, step: 0.1 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="Agency Cost %"
								type="number"
								value={agencyCostPercentage}
								onChange={(e) => setAgencyCostPercentage(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, step: 1 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="HPPD Lower Threshold"
								type="number"
								value={hppdLowerThreshold}
								onChange={(e) => setHppdLowerThreshold(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, max: 10, step: 0.1 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="HPPD Upper Threshold"
								type="number"
								value={hppdUpperThreshold}
								onChange={(e) => setHppdUpperThreshold(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, max: 10, step: 0.1 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="Target OT Reduction %"
								type="number"
								value={targetOtReduction}
								onChange={(e) => setTargetOtReduction(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, max: 100, step: 1 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="Target Overstaffed Reduction %"
								type="number"
								value={targetOverstaffedReduction}
								onChange={(e) => setTargetOverstaffedReduction(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, max: 100, step: 1 } }}
							/>
						</Grid>
						<Grid size={{ xs: 12, md: 2 }}>
							<TextField
								fullWidth
								label="Target Agency Reduction %"
								type="number"
								value={targetAgencyReduction}
								onChange={(e) => setTargetAgencyReduction(+e.target.value)}
								slotProps={{ htmlInput: { min: 0, max: 100, step: 1 } }}
							/>
						</Grid>
						<Grid size={12}>
							<SystemButton onClick={handleCalculate} disabled={!selectedFacility} loading={isPending} sx={{ mt: 1, width: "100%" }}>
								Create Report
							</SystemButton>
						</Grid>
					</Grid>
				</CardContent>
			</Card>
		</Box>
	);
};
